/**
 * A ordem das listas de cadastro global, e o destaque do que acabou de entrar.
 *
 * **Por que existe.** Os cadastros globais listavam em ordem alfabética, e o
 * registro recém-incluído sumia no meio de 1.662 sistemas — às vezes depois do
 * corte de 200 linhas desenhadas. Quem incluía não tinha como saber se tinha
 * dado certo sem filtrar pelo nome que acabou de digitar.
 *
 * Duas peças resolvem:
 * - a **ordem por inclusão** (os mais recentes primeiro) é o padrão, com a
 *   alfabética a um clique. "Recentes primeiro", e não no fim, por causa do
 *   corte: no fim, o recém-incluído cairia justamente fora do que se desenha;
 * - o **recém-incluído** fica realçado alguns segundos e a lista rola até ele,
 *   seja qual for a ordem escolhida.
 *
 * O id é o relógio da inclusão: as chaves manuais são `MAX + 1` e as outras são
 * sequência, então id maior é registro mais novo.
 */

import { useCallback, useEffect, useRef, useState } from "react"

export type OrdemDaLista = "inclusao" | "alfabetica"

const PREFIXO = "sistema-oc.ordem."

function lerGravada(tela: string): OrdemDaLista {
  try {
    return localStorage.getItem(PREFIXO + tela) === "alfabetica" ? "alfabetica" : "inclusao"
  } catch {
    return "inclusao"
  }
}

/** A ordem escolhida, lembrada por tela neste navegador. */
export function usarOrdemDaLista(tela: string) {
  const [ordem, setOrdem] = useState<OrdemDaLista>(() => lerGravada(tela))
  const trocar = useCallback((nova: OrdemDaLista) => {
    setOrdem(nova)
    try { localStorage.setItem(PREFIXO + tela, nova) } catch { /* sem armazenamento */ }
  }, [tela])
  return [ordem, trocar] as const
}

/** Ordena uma cópia: por inclusão, os mais recentes primeiro; ou pelo nome. */
export function ordenarLista<T>(
  itens: readonly T[], ordem: OrdemDaLista, idDe: (x: T) => number, nomeDe: (x: T) => string,
): T[] {
  const copia = [...itens]
  if (ordem === "inclusao") return copia.sort((a, b) => idDe(b) - idDe(a))
  return copia.sort((a, b) =>
    nomeDe(a).localeCompare(nomeDe(b), "pt-BR", { sensitivity: "base", numeric: true })
    || idDe(a) - idDe(b))
}

export function SeletorDeOrdem({ valor, aoMudar, estilo }: {
  valor: OrdemDaLista
  aoMudar: (o: OrdemDaLista) => void
  estilo?: React.CSSProperties
}) {
  return (
    <select
      value={valor}
      onChange={(e) => aoMudar(e.target.value as OrdemDaLista)}
      aria-label="Ordem da lista"
      title="Ordem da lista"
      style={{
        height: 28, padding: "0 7px", fontSize: 12, borderRadius: 7,
        border: "1px solid var(--line2)", background: "var(--surface2)", color: "var(--txt)",
        ...estilo,
      }}
    >
      <option value="inclusao">Mais recentes primeiro</option>
      <option value="alfabetica">Ordem alfabética</option>
    </select>
  )
}

/**
 * A mesma escolha do `SeletorDeOrdem`, num botão pequeno que alterna entre as duas —
 * para caber ao lado de um filtro numa coluna estreita, onde o select de "Mais
 * recentes primeiro" ocupava uma linha inteira.
 *
 * O rótulo mostra a ordem **atual** ("Recentes" ou "A–Z"); o que o clique faz vai no
 * `title` e no nome acessível, para ninguém precisar adivinhar.
 */
export function BotaoDeOrdem({ valor, aoMudar }: {
  valor: OrdemDaLista
  aoMudar: (o: OrdemDaLista) => void
}) {
  const alfabetica = valor === "alfabetica"
  const explicacao = alfabetica
    ? "Ordem alfabética. Clique para ver os mais recentes primeiro."
    : "Mais recentes primeiro. Clique para ordem alfabética."
  return (
    <button
      type="button"
      onClick={() => aoMudar(alfabetica ? "inclusao" : "alfabetica")}
      aria-label={`Ordem: ${explicacao}`}
      title={explicacao}
      style={{
        flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: 4,
        padding: "0 8px", fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: "pointer",
        border: "1px solid var(--line2)", background: "var(--surface2)", color: "var(--txt2)",
        whiteSpace: "nowrap",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
           style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
        <path d="M7 4v16M3 16l4 4 4-4M14 6h7M14 12h5M14 18h3" />
      </svg>
      {alfabetica ? "A–Z" : "Recentes"}
    </button>
  )
}

/** Quanto tempo o realce fica. Tempo de achar a linha com o olho, não de incomodar. */
export const DURACAO_DO_REALCE_MS = 6000

/**
 * O que acabou de entrar. Some sozinho depois de {@link DURACAO_DO_REALCE_MS};
 * marcar de novo reinicia a contagem.
 *
 * `id` é o primeiro marcado — é até ele que a lista rola. `tem` responde por
 * todos: um lote marca várias linhas de uma vez.
 */
export function usarRecemIncluido() {
  const [ids, setIds] = useState<readonly number[]>([])
  const relogio = useRef<ReturnType<typeof setTimeout>>()

  const marcar = useCallback((novo: number | readonly number[] | null | undefined) => {
    clearTimeout(relogio.current)
    const lista = novo == null ? [] : typeof novo === "number" ? [novo] : novo
    setIds(lista)
    if (lista.length > 0) relogio.current = setTimeout(() => setIds([]), DURACAO_DO_REALCE_MS)
  }, [])

  useEffect(() => () => clearTimeout(relogio.current), [])
  const tem = useCallback((x: number) => ids.includes(x), [ids])
  return { id: ids[0] ?? null, tem, marcar }
}

/**
 * Rola até o elemento `[data-recem-incluido]` dentro de `raiz` quando `id` muda.
 * Espera a lista chegar: a linha só existe depois que a consulta relê.
 */
export function usarRolarAteRecente(raiz: React.RefObject<HTMLElement>, id: number | null, pronto: unknown) {
  useEffect(() => {
    if (id == null) return
    const alvo = raiz.current?.querySelector<HTMLElement>("[data-recem-incluido]")
    alvo?.scrollIntoView?.({ block: "center", behavior: "smooth" })
  }, [raiz, id, pronto])
}
